import {
  clampConfidence,
  confidenceThresholdForTerm,
  visibilityByTerm,
  visibilityForTerm
} from './term-visualization';

export const MALARIA_MODEL_TYPE = 'malaria';
export const MALARIA_CONFIDENCE_HEATMAP_TYPE = 'confidence_heatmap';

const DEFAULT_WBC_PER_MICROLITER = 8000;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.65;
const DEFAULT_PARASITE_CONFIDENCE_THRESHOLD = 0.65;
const MALARIA_PARASITE_TERM = 'Malaria_Detected';
const MALARIA_WBC_TERMS = ['WBC_Detected', 'WBC', 'White_Blood_Cell', 'White Blood Cell'];
const MALARIA_DETECTION_TYPES = ['parasite', 'wbc'];
const MALARIA_RUNNER_IDENTIFIERS = [
  'malaria',
  'thick_ai',
  'thick blood',
  'thick blood smear',
  'thick blood film'
];

export function isMalariaRunner(runnerName = '') {
  const normalizedName = String(runnerName).toLowerCase();
  return MALARIA_RUNNER_IDENTIFIERS.some(identifier => normalizedName.includes(identifier));
}

function toFiniteNumber(value, fallback = 0) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function defaultConfidenceThresholdForType(type) {
  return type === 'parasite' ? DEFAULT_PARASITE_CONFIDENCE_THRESHOLD : DEFAULT_CONFIDENCE_THRESHOLD;
}

function confidenceThresholdForType(result, type) {
  return confidenceThresholdForTerm({
    result,
    type,
    defaultThreshold: defaultConfidenceThresholdForType(type)
  });
}

function heatmapVisibilityForType(result, type) {
  return visibilityForTerm(result?.heatmap, type, false);
}

function visibilityByType(section, defaultVisible = true) {
  return visibilityByTerm(MALARIA_DETECTION_TYPES, section, defaultVisible);
}

function confidenceLabelsVisibilityByType(section) {
  return visibilityByTerm(MALARIA_DETECTION_TYPES, section, false, 'showConfidenceLabelsByType');
}

function normalizeDetectionType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === MALARIA_PARASITE_TERM.toLowerCase() || normalized === 'parasite') {
    return 'parasite';
  }
  if (MALARIA_WBC_TERMS.map(term => term.toLowerCase()).includes(normalized) || ['wbc', 'white_blood_cell', 'white blood cell'].includes(normalized)) {
    return 'wbc';
  }
  return normalized;
}

function freezeArray(items = []) {
  return Object.freeze(items);
}

function freezeDetection(detection) {
  if (!detection || Object.isFrozen(detection)) {
    return detection;
  }
  return Object.freeze(detection);
}

export function freezeMalariaDetections(detections = []) {
  if (Object.isFrozen(detections)) {
    return detections;
  }
  return freezeArray((Array.isArray(detections) ? detections : []).map(freezeDetection));
}

function centroidCoordinate(raw, axis) {
  const centroid = raw?.centroid || raw?.location?.centroid;
  return raw?.[axis] ?? raw?.location?.[axis] ?? centroid?.[axis];
}

function normalizeCentroid(raw, x, y) {
  const centroid = raw?.centroid || raw?.location?.centroid;
  const centroidX = centroid?.x ?? x;
  const centroidY = centroid?.y ?? y;
  if (!Number.isFinite(Number(centroidX)) || !Number.isFinite(Number(centroidY))) {
    return null;
  }
  return {
    x: Number(centroidX),
    y: Number(centroidY)
  };
}

function normalizeDetection(raw, index) {
  const type = normalizeDetectionType(raw?.type || raw?.class || raw?.name || raw?.termName || raw?.label);
  const x = toFiniteNumber(centroidCoordinate(raw, 'x'));
  const y = toFiniteNumber(centroidCoordinate(raw, 'y'));
  const centroid = normalizeCentroid(raw, x, y);
  return {
    id: raw?.id || `${type || 'detection'}-${index + 1}`,
    type,
    confidence: Math.max(0, Math.min(1, toFiniteNumber(raw?.confidence))),
    x,
    y,
    centroid,
    location: typeof raw?.location === 'string' ? raw.location : null,
    geometryCompression: raw?.geometryCompression,
    class: raw?.class,
    created: raw?.created,
    updated: raw?.updated,
    user: raw?.user,
    image: raw?.image,
    project: raw?.project,
    slice: raw?.slice,
    term: raw?.term,
    userByTerm: raw?.userByTerm,
    area: raw?.area,
    areaUnit: raw?.areaUnit,
    perimeter: raw?.perimeter,
    perimeterUnit: raw?.perimeterUnit,
    termName: raw?.termName || raw?.name || raw?.label,
    aiVisualizationType: raw?.aiVisualizationType,
    cropURL: raw?.cropURL,
    smallCropURL: raw?.smallCropURL,
    included: raw?.included !== false
  };
}

export function normalizeMalariaAnnotationDetections(annotations = []) {
  return freezeMalariaDetections(
    annotations
      .filter(annotation => annotation?.aiVisualizationType === MALARIA_CONFIDENCE_HEATMAP_TYPE)
      .map((annotation, index) => normalizeDetection({
        ...annotation,
        id: annotation.id,
        type: annotation.termName || annotation.name || annotation.label,
        confidence: annotation.confidence,
        x: annotation.x,
        y: annotation.y,
        centroid: annotation.centroid,
        location: annotation.location,
        geometryCompression: annotation.geometryCompression
      }, index))
      .filter(detection => ['parasite', 'wbc'].includes(detection.type))
  );
}

function finiteCoordinatePair(pair) {
  if (!Array.isArray(pair) || pair.length < 2) {
    return null;
  }
  const x = Number(pair[0]);
  const y = Number(pair[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return [x, y];
}

function polygonRingForFeature(feature) {
  const geometry = feature?.geometry || {};
  if (geometry.type !== 'Polygon' || !Array.isArray(geometry.coordinates?.[0])) {
    return [];
  }
  return geometry.coordinates[0]
    .map(finiteCoordinatePair)
    .filter(Boolean);
}

function wktPolygonForRing(ring) {
  if (!ring.length) {
    return null;
  }
  const closedRing = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
    ? ring
    : [...ring, ring[0]];
  return `POLYGON ((${closedRing.map(([x, y]) => `${x} ${y}`).join(', ')}))`;
}

function centroidForRing(ring) {
  if (!ring.length) {
    return null;
  }
  const xs = ring.map(([x]) => x);
  const ys = ring.map(([, y]) => y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2
  };
}

function normalizeGeojsonFeatureDetection(feature, index) {
  const properties = feature?.properties || {};
  const ring = polygonRingForFeature(feature);
  const centroid = centroidForRing(ring);
  return normalizeDetection({
    id: feature?.id || `geojson-feature-${index + 1}`,
    name: properties.name,
    label: properties.label,
    termName: properties.termName,
    confidence: properties.confidence,
    centroid,
    location: wktPolygonForRing(ring),
    geometryCompression: false,
    aiVisualizationType: MALARIA_CONFIDENCE_HEATMAP_TYPE
  }, index);
}

function normalizeMalariaGeojsonDetections(geojson) {
  return (Array.isArray(geojson?.features) ? geojson.features : [])
    .map(normalizeGeojsonFeatureDetection)
    .filter(detection => ['parasite', 'wbc'].includes(detection.type));
}

export function includedMalariaDetections(result) {
  return (result?.detections || []).filter(detection => (
    detection.included !== false && detection.confidence >= confidenceThresholdForType(result, detection.type)
  ));
}

function buildDetectionSummary(result) {
  const includedByType = {
    parasite: [],
    wbc: []
  };
  const totalByType = {
    parasite: 0,
    wbc: 0
  };
  const confidenceSumByType = {
    parasite: 0,
    wbc: 0
  };

  (result?.detections || []).forEach(detection => {
    const type = normalizeDetectionType(detection.type);
    if (!MALARIA_DETECTION_TYPES.includes(type)) {
      return;
    }
    totalByType[type] += 1;
    if (detection.included === false || detection.confidence < confidenceThresholdForType(result, type)) {
      return;
    }
    includedByType[type].push(detection);
    confidenceSumByType[type] += detection.confidence;
  });

  const sortedIncludedByType = MALARIA_DETECTION_TYPES.reduce((acc, type) => ({
    ...acc,
    [type]: freezeArray(includedByType[type].slice().sort((a, b) => b.confidence - a.confidence))
  }), {});

  return Object.freeze({
    includedByType: Object.freeze({
      parasite: freezeArray(includedByType.parasite),
      wbc: freezeArray(includedByType.wbc)
    }),
    sortedIncludedByType,
    countByType: Object.freeze({
      parasite: includedByType.parasite.length,
      wbc: includedByType.wbc.length
    }),
    totalByType: Object.freeze(totalByType),
    meanConfidenceByType: Object.freeze({
      parasite: includedByType.parasite.length ? confidenceSumByType.parasite / includedByType.parasite.length : null,
      wbc: includedByType.wbc.length ? confidenceSumByType.wbc / includedByType.wbc.length : null
    })
  });
}

export function malariaCounts(result) {
  const included = includedMalariaDetections(result);
  return {
    parasites: included.filter(detection => detection.type === 'parasite'),
    wbcs: included.filter(detection => ['wbc', 'white_blood_cell', 'white blood cell'].includes(detection.type))
  };
}

export function calculateMalariaDensity(parasiteCount, wbcCount, wbcPerMicroliter = DEFAULT_WBC_PER_MICROLITER) {
  if (!wbcCount) {
    return null;
  }
  return parasiteCount * wbcPerMicroliter / wbcCount;
}

export function recomputeMalariaResult(result, options = {}) {
  const includeVisualizations = options.includeVisualizations !== false;
  const fallbackThreshold = clampConfidence(result?.thresholds?.confidence, DEFAULT_CONFIDENCE_THRESHOLD);
  const existingByTypeThresholds = result.thresholds?.byType || {};
  const thresholds = {
    ...result.thresholds,
    confidence: fallbackThreshold,
    byType: {
      ...existingByTypeThresholds
    }
  };
  thresholds.byType.parasite = confidenceThresholdForType(result, 'parasite');
  thresholds.byType.wbc = confidenceThresholdForType(result, 'wbc');

  const normalizedResult = {
    ...result,
    thresholds
  };
  const detectionSummary = buildDetectionSummary(normalizedResult);
  const parasites = detectionSummary.includedByType.parasite;
  const wbcs = detectionSummary.includedByType.wbc;
  const wbcPerMicroliter = toFiniteNumber(normalizedResult?.density?.wbcPerMicroliter, DEFAULT_WBC_PER_MICROLITER);
  const parasitesPerMicroliter = calculateMalariaDensity(parasites.length, wbcs.length, wbcPerMicroliter);
  const warnings = (normalizedResult.warnings || []).filter(warning => warning.code !== 'NO_INCLUDED_WBC');
  const aiStatus = parasites.length > 0 ? 'Detected' : 'Not Detected';
  const includedDetections = [...parasites, ...wbcs];
  const showConfidenceLabelsByType = confidenceLabelsVisibilityByType(normalizedResult.overlay);
  const overlayPoints = includeVisualizations
    ? includedDetections.map(detection => ({
      id: detection.id,
      type: detection.type,
      confidence: detection.confidence,
      x: detection.x,
      y: detection.y,
      centroid: detection.centroid,
      location: detection.location,
      geometryCompression: detection.geometryCompression,
      class: detection.class,
      created: detection.created,
      updated: detection.updated,
      user: detection.user,
      image: detection.image,
      project: detection.project,
      slice: detection.slice,
      term: detection.term,
      userByTerm: detection.userByTerm,
      area: detection.area,
      areaUnit: detection.areaUnit,
      perimeter: detection.perimeter,
      perimeterUnit: detection.perimeterUnit,
      termName: detection.termName,
      aiVisualizationType: detection.aiVisualizationType,
      cropURL: detection.cropURL,
      smallCropURL: detection.smallCropURL,
      showConfidenceLabel: showConfidenceLabelsByType[detection.type] !== false,
      visible: true
    }))
    : (normalizedResult.overlay?.annotations || normalizedResult.overlay?.points || []);
  const heatmapPoints = includeVisualizations
    ? includedDetections.map(detection => ({
      id: detection.id,
      type: detection.type,
      x: detection.x,
      y: detection.y,
      weight: detection.confidence
    }))
    : (normalizedResult.heatmap?.points || []);

  if (parasites.length > 0 && wbcs.length === 0) {
    warnings.push({
      code: 'NO_INCLUDED_WBC',
      messageKey: 'ai-warning-no-included-wbc'
    });
  }

  return {
    ...normalizedResult,
    thresholds,
    warnings,
    summary: {
      ...normalizedResult.summary,
      parasiteCount: parasites.length,
      wbcCount: wbcs.length,
      status: aiStatus
    },
    density: {
      ...normalizedResult.density,
      wbcPerMicroliter,
      parasitesPerMicroliter
    },
    derived: detectionSummary,
    heatmap: {
      ...normalizedResult.heatmap,
      visible: MALARIA_DETECTION_TYPES.some(type => heatmapVisibilityForType(normalizedResult, type)),
      byTypeVisible: visibilityByType(normalizedResult.heatmap, false),
      points: heatmapPoints
    },
    overlay: {
      ...normalizedResult.overlay,
      byTypeVisible: visibilityByType(normalizedResult.overlay, true),
      showConfidenceLabelsByType,
      annotations: overlayPoints,
      points: overlayPoints
    },
    report: {
      ...normalizedResult.report,
      status: aiStatus,
      parasitesPerMicroliter
    }
  };
}

export function formatMalariaResults(jobId, runnerName, data = {}, needsResultSync = false) {
  const rawDetections = [
    ...(Array.isArray(data?.detections) ? data.detections : []),
    ...normalizeMalariaGeojsonDetections(data?.geojson)
  ];
  const hasGlobalThreshold = data?.confidenceThreshold !== undefined || data?.thresholds?.confidence !== undefined;
  const threshold = clampConfidence(data?.confidenceThreshold ?? data?.thresholds?.confidence, DEFAULT_CONFIDENCE_THRESHOLD);
  const byTypeThresholds = data?.thresholds?.byType || {};
  const rawParasiteCount = toFiniteNumber(data?.[MALARIA_PARASITE_TERM]?.count, 0);
  const detections = freezeMalariaDetections(
    rawDetections
      .map(normalizeDetection)
      .filter(detection => ['parasite', 'wbc'].includes(detection.type))
  );

  const result = {
    id: jobId,
    runnerName,
    modelType: MALARIA_MODEL_TYPE,
    needsResultSync,
    visible: true,
    isMalaria: true,
    showAllAnnotations: true,
    rawData: data || {},
    detections,
    annotations: [],
    warnings: [],
    thresholds: {
      confidence: threshold,
      byType: {
        parasite: clampConfidence(
          byTypeThresholds.parasite ?? (hasGlobalThreshold ? threshold : DEFAULT_PARASITE_CONFIDENCE_THRESHOLD),
          DEFAULT_PARASITE_CONFIDENCE_THRESHOLD
        ),
        wbc: clampConfidence(
          byTypeThresholds.wbc ?? (hasGlobalThreshold ? threshold : DEFAULT_CONFIDENCE_THRESHOLD),
          DEFAULT_CONFIDENCE_THRESHOLD
        )
      }
    },
    sectionCollapsed: {
      summary: false,
      density: false,
      report: false
    },
    summary: {
      status: data?.status || 'Not Detected',
      species: data?.species || 'N/A',
      parasiteCount: 0,
      rawParasiteCount,
      wbcCount: 0,
      aiConfidence: toFiniteNumber(data?.aiConfidence ?? data?.confidence),
      imageQuality: data?.imageQuality || 'N/A',
      reviewStatus: data?.reviewStatus || 'Pending'
    },
    density: {
      wbcPerMicroliter: toFiniteNumber(data?.wbcPerMicroliter, DEFAULT_WBC_PER_MICROLITER),
      parasitesPerMicroliter: null
    },
    heatmap: {
      visible: false,
      byTypeVisible: visibilityByType(null, false),
      opacity: 0.8,
      radius: 18,
      blur: 22,
      points: []
    },
    overlay: {
      visible: true,
      byTypeVisible: visibilityByType(null, true),
      showConfidenceLabelsByType: confidenceLabelsVisibilityByType(null),
      opacity: 0.9,
      points: []
    },
    report: {
      status: data?.status ?? null,
      species: data?.species || 'Unknown / Not specified',
      wbcPerMicroliter: toFiniteNumber(data?.wbcPerMicroliter, DEFAULT_WBC_PER_MICROLITER),
      parasitesPerMicroliter: data?.parasitesPerMicroliter ?? null,
      reviewStatus: data?.reviewStatus || 'Pending'
    },
    scoring: {}
  };

  return recomputeMalariaResult(result);
}

export function createMockMalariaPayload() {
  const parasitePoints = Array.from({ length: 432 }, (_, index) => ({
    id: `p-${index + 1}`,
    name: MALARIA_PARASITE_TERM,
    confidence: Number((0.35 + ((index % 60) / 100)).toFixed(2)),
    x: 200 + ((index * 173) % 39000),
    y: 20750 + ((index * 197) % 9800)
  }));
  const wbcPoints = Array.from({ length: 200 }, (_, index) => ({
    id: `w-${index + 1}`,
    name: 'WBC_Detected',
    confidence: Number((0.42 + ((index % 50) / 100)).toFixed(2)),
    x: 800 + ((index * 187) % 38000),
    y: 21000 + ((index * 211) % 9400)
  }));

  return {
    status: 'Detected',
    [MALARIA_PARASITE_TERM]: {
      count: parasitePoints.length
    },
    WBC_Detected: {
      count: wbcPoints.length
    },
    species: 'Unknown / Not specified',
    wbcPerMicroliter: DEFAULT_WBC_PER_MICROLITER,
    reviewStatus: 'Pending',
    detections: [
      ...parasitePoints,
      ...wbcPoints
    ]
  };
}
